import { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetJobAssessmentQuery, useUserSaveAssessmentMutation } from 'services';
import useTranslate from 'hooks/useTranslate';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Loader from 'components/Common/Loader';
import { setModalConfig } from 'store/common';
import toast from 'react-hot-toast';
import './styles.scss';

const SubmitAssessment = () => {
	const { id } = useParams<any>();
	const navigate = useNavigate();
	const {
		data: {
			jobQuestionList: {
				assessmentQuestions = [],
				totalQuestion = 0,
				id: assessmentId = null,
			} = {},
			isError = false,
		} = {},
	} = useGetJobAssessmentQuery(id);
	const [questions, setQuestions] = useState<any>({});
	const [currentIndex, setCurrentIndex] = useState(0);
	const [attemptedQuestions, setAttemptedQuestions] = useState<any>([]);

	useEffect(() => {
		if (isError) navigate(-1);
	}, [isError]);

	useEffect(() => {
		if (assessmentQuestions.length > 0) setQuestions(assessmentQuestions[currentIndex] || {});
	}, [currentIndex, assessmentQuestions]);

	const { translate } = useTranslate();
	const { mutateAsync: saveAssessment, isPending } = useUserSaveAssessmentMutation();

	const handleClick = () => {
		navigate(`/job-detail/${id}`);
	};

	const onHandleFinish = () => {
		const finalData: any = {
			jobId: id,
			assessStatus: 'START',
			attemptedQuestions: attemptedQuestions,
		};
		saveAssessment(finalData)
			.then((res: any) => {
				if (res?.success) {
					formik.resetForm();
					setModalConfig({
						visible: true,
						id,
						onClick: handleClick,
						type: 'successAssessmentModal',
					});
				}
			})
			.catch((error) => {
				console.error('Failed to save assessment:', error);
				toast.error(translate('ToastMessage.titleFailed', { title: 'Assessment' })); // Notify the user of the failure
			});
	};

	const formik: any = useFormik({
		initialValues: {
			answer: '',
		},
		enableReinitialize: true,
		validationSchema: Yup.object().shape({
			answer: Yup.string().required(translate('Validation.required', { field: 'Answer' })),
		}),
		onSubmit: (values: any) => {
			if (totalQuestion === currentIndex + 1) {
				const finalData: any = {
					jobId: id,
					assessStatus: 'END',
					attemptedQuestions: [
						...attemptedQuestions,
						{
							questionId: questions?.id,
							answer: values?.answer,
						},
					],
				};
				saveAssessment(finalData)
					.then((res: any) => {
						if (res?.success) {
							formik.resetForm();
							setModalConfig({
								visible: true,
								id,
								onClick: handleClick,
								type: 'successAssessmentModal',
							});
						}
					})
					.catch(() => {});
			} else {
				setAttemptedQuestions([
					...attemptedQuestions,
					{
						questionId: questions?.id,
						answer: values?.answer,
					},
				]);
				formik.resetForm();
				setCurrentIndex(currentIndex + 1);
			}
		},
	});

	return (
		<div className="questionnaire-box mx-auto bg-white">
			<div className="questionnaire-box-heading">
				<h3 className="fw-bold">{translate('Questionnaire.questionnaire')}</h3>
				<p className="mb-0">
					{translate('Questionnaire.question')} {currentIndex + 1}{' '}
					{translate('Questionnaire.outOf')} {totalQuestion}
				</p>
				<hr></hr>
			</div>
			<Form onSubmit={formik.handleSubmit}>
				{Object.keys(questions)?.length > 0 && (
					<div className="question-content">
						<h4 className="fw-bold">{questions?.question}</h4>
						<div className="question-content-options d-flex flex-column flex-md-row flex-wrap gap-3">
							{questions?.options?.length > 0 &&
								questions.options.map((option: any, index: number) => {
									return (
										<div key={`${index}-${option}`} className="option-box rounded-pill">
											<Form.Check
												inline
												label={option}
												type="checkbox"
												name="answer"
												value={option}
												checked={formik.values.answer === option}
												onChange={() => formik.setFieldValue('answer', option)}
												className="m-0"
											/>
										</div>
									);
								})}
							{formik?.errors?.answer && (
								<span
									className="text-danger"
									style={{ fontSize: '12px', fontWeight: 600, marginLeft: '20px' }}
								>
									{formik.errors.answer}
								</span>
							)}
						</div>
					</div>
				)}
				<div className="question-actions d-flex justify-content-end gap-4">
					{currentIndex > 0 && currentIndex + 1 < totalQuestion && (
						<Button
							className="filled-btn rounded-pill"
							onClick={() => onHandleFinish()}
							disabled={isPending}
						>
							{isPending ? <Loader /> : 'Finish'}
						</Button>
					)}
					<Button className="filled-btn rounded-pill" type="submit" disabled={isPending}>
						{isPending ? (
							<Loader />
						) : currentIndex + 1 !== totalQuestion ? (
							translate('Questionnaire.next')
						) : (
							translate('Questionnaire.finish')
						)}
					</Button>
				</div>
			</Form>
		</div>
	);
};

export default SubmitAssessment;
